import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Space,
  Tooltip,
  Upload,
} from 'antd';
import { cartonApi, supplierApi, customerApi, productApi, type CartonDTO, type CreateCartonRequest, type CartonProductItemDTO, type ProductDTO, type ReceiveCartonRequest } from '../api/kfc';
import CartonDetailDrawer from '../components/CartonDetailDrawer';

const STATE_LABELS: Record<string, string> = {
  preparing: '待收货',
  inKFYWarehouse: '抵达凯飞越仓库',
  inAgentWarehouse: '抵达代理仓库(出发地)',
  readyForShipping: '准备运输',
  onTheWay: '在途运输',
  arrivedDstPort: '抵达目的港',
  recevied: '已签收',
};

const CartonList: React.FC = () => {
  const [list, setList] = useState<CartonDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [suppliers, setSuppliers] = useState<{ value: string; label: string }[]>([]);
  const [customers, setCustomers] = useState<{ value: string; label: string }[]>([]);
  const [form] = Form.useForm();
  const [cartonRows, setCartonRows] = useState<Partial<CreateCartonRequest>[]>([
    { cartonId: '', fromSupplierId: undefined, belongClientId: undefined, products: [{ hscode: '', amount: 1 }] },
  ]);
  const [submitting, setSubmitting] = useState(false);
  /** 货物详情滑窗（复用 CartonDetailDrawer） */
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailCartonId, setDetailCartonId] = useState<string | undefined>(undefined);
  /** 表格多选：选中的行 id 列表 */
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  /** 收货弹窗 */
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [receiveTargetIds, setReceiveTargetIds] = useState<string[]>([]);
  const [receiveSubmitting, setReceiveSubmitting] = useState(false);
  const [receiveForm] = Form.useForm();
  /** 收货照片：{ 描述 -> base64 }，表单项为列表 { desc, base64 } */
  const [receivePictures, setReceivePictures] = useState<{ desc: string; base64: string }[]>([]);

  const load = () => {
    setLoading(true);
    cartonApi
      .list()
      .then((res) => setList(res.data || []))
      .catch(() => {
        setList([]);
        message.warning('无法加载货物列表');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    supplierApi.list().then((res) => {
      const data = (res.data || []).map((s) => ({ value: s.id!, label: `${s.supplierName || s.supplierId || s.id}` }));
      setSuppliers(data);
    }).catch(() => setSuppliers([]));
    customerApi.list().then((res) => {
      const data = (res.data || []).map((c) => ({ value: c.id!, label: `${c.name || c.customerCode || c.id}` }));
      setCustomers(data);
    }).catch(() => setCustomers([]));
  }, []);

  const openDetail = (record: CartonDTO) => {
    if (!record.id) return;
    setDetailCartonId(record.id);
    setDetailDrawerOpen(true);
  };

  const closeDetail = () => {
    setDetailDrawerOpen(false);
    setDetailCartonId(undefined);
  };

  const openAdd = () => {
    setCartonRows([
      { cartonId: '', fromSupplierId: undefined, belongClientId: undefined, products: [{ hscode: '', amount: 1 }] },
    ]);
    form.resetFields();
    setAddModalOpen(true);
  };

  const addCartonRow = () => {
    setCartonRows((prev) => [
      ...prev,
      { cartonId: '', fromSupplierId: undefined, belongClientId: undefined, products: [{ hscode: '', amount: 1 }] },
    ]);
  };

  const removeCartonRow = (index: number) => {
    setCartonRows((prev) => prev.filter((_, i) => i !== index));
  };

  const onAddProductsChange = (cartonIndex: number, products: { hscode?: string; amount?: number }[]) => {
    setCartonRows((prev) => {
      const next = [...prev];
      next[cartonIndex] = { ...next[cartonIndex], products: products.length ? products : [{ hscode: '', amount: 1 }] };
      return next;
    });
  };

  const onAddCartonId = (index: number, cartonId: string) => {
    setCartonRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], cartonId };
      return next;
    });
  };

  const onAddSupplier = (index: number, fromSupplierId: string | undefined) => {
    setCartonRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], fromSupplierId };
      return next;
    });
  };

  const onAddClient = (index: number, belongClientId: string | undefined) => {
    setCartonRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], belongClientId };
      return next;
    });
  };

  const submitAdd = () => {
    const cartons: CreateCartonRequest[] = cartonRows
      .map((r) => ({
        cartonId: (r.cartonId || '').trim(),
        fromSupplierId: r.fromSupplierId || undefined,
        belongClientId: r.belongClientId || undefined,
        products: (r.products || []).filter((p) => p.hscode && (p.amount ?? 0) > 0).map((p) => ({ hscode: p.hscode!, amount: p.amount ?? 1 })),
      }))
      .filter((c) => c.cartonId && c.products.length > 0);
    if (cartons.length === 0) {
      message.warning('请至少录入一箱货物，且每箱需填写货物标识和至少一种产品（海关编码+数量）');
      return;
    }
    setSubmitting(true);
    cartonApi
      .createCartons({ cartons })
      .then(() => {
        message.success(`成功录入 ${cartons.length} 箱货物，状态为待收货`);
        setAddModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '录入失败'))
      .finally(() => setSubmitting(false));
  };

  const renderState = (state: string) => STATE_LABELS[state] || state;

  /** 打开收货弹窗：ids 为要收货的货物 id 列表（行操作传单条，工具栏传选中） */
  const openReceiveModal = (ids: string[]) => {
    const validIds = ids.filter(Boolean);
    if (validIds.length === 0) {
      message.warning('请先选择要收货的货物');
      return;
    }
    setReceiveTargetIds(validIds);
    receiveForm.resetFields();
    setReceivePictures([]);
    setReceiveModalOpen(true);
  };

  const closeReceiveModal = () => {
    setReceiveModalOpen(false);
    setReceiveTargetIds([]);
    setReceivePictures([]);
  };

  const addReceivePicture = () => {
    setReceivePictures((prev) => [...prev, { desc: '', base64: '' }]);
  };

  const removeReceivePicture = (index: number) => {
    setReceivePictures((prev) => prev.filter((_, i) => i !== index));
  };

  const setReceivePictureDesc = (index: number, desc: string) => {
    setReceivePictures((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], desc };
      return next;
    });
  };

  const setReceivePictureBase64 = (index: number, base64: string) => {
    setReceivePictures((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], base64 };
      return next;
    });
  };

  const submitReceive = () => {
    receiveForm.validateFields().then((values) => {
      const pictures: Record<string, string> = {};
      receivePictures.forEach((p) => {
        const desc = (p.desc || '照片').trim();
        if (p.base64) pictures[desc] = p.base64;
      });
      const body: ReceiveCartonRequest = {
        arrivedBy: values.arrivedBy,
        queryParamOfShippment: values.queryParamOfShippment?.trim() || undefined,
        pictures: Object.keys(pictures).length ? pictures : undefined,
      };
      setReceiveSubmitting(true);
      const run = () => {
        if (receiveTargetIds.length === 1) {
          return cartonApi.receive(receiveTargetIds[0], body).then((r) => [r.data]);
        }
        return cartonApi.receiveBatch({ cartonIds: receiveTargetIds, ...body }).then((r) => r.data || []);
      };
      run()
        .then(() => {
          message.success(`已收货 ${receiveTargetIds.length} 箱`);
          closeReceiveModal();
          setSelectedRowKeys([]);
          load();
        })
        .catch((e) => message.error(e.response?.data?.message || '收货失败'))
        .finally(() => setReceiveSubmitting(false));
    });
  };

  return (
    <>
      <Card
        title="货物列表"
        extra={
          <Space>
            <Button type="primary" onClick={openAdd}>
              录入货物
            </Button>
            <Button onClick={() => openReceiveModal(selectedRowKeys as string[])} disabled={selectedRowKeys.length === 0}>
              收货
            </Button>
            <Tooltip title="敬请期待">
              <Button disabled>交代理仓</Button>
            </Tooltip>
          </Space>
        }
      >
        {selectedRowKeys.length > 0 && (
          <Space style={{ marginBottom: 12 }}>
            <span style={{ color: '#666' }}>已选 <strong>{selectedRowKeys.length}</strong> 条</span>
            <Button size="small" onClick={() => setSelectedRowKeys([])}>清空选择</Button>
            <Button size="small" onClick={() => openReceiveModal(selectedRowKeys as string[])}>批量收货</Button>
            <Tooltip title="敬请期待">
              <Button size="small" disabled>批量交代理仓</Button>
            </Tooltip>
          </Space>
        )}
        <Table
          loading={loading}
          dataSource={list}
          rowKey="id"
          scroll={{ x: 900 }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          onRow={(record) => ({
            onClick: (e) => {
              if ((e.target as HTMLElement).closest('.ant-table-selection-column')) return;
              openDetail(record);
            },
            style: { cursor: 'pointer' },
          })}
          columns={[
            { title: '货物标识', dataIndex: 'cartonId', width: 160, ellipsis: true },
            { title: '供应商', dataIndex: 'fromSupplierId', width: 120, ellipsis: true, render: (id) => suppliers.find((s) => s.value === id)?.label || id || '-' },
            { title: '客户', dataIndex: 'belongClientId', width: 120, ellipsis: true, render: (id) => customers.find((c) => c.value === id)?.label || id || '-' },
            { title: '状态', dataIndex: 'state', width: 120, render: (s) => renderState(s || '') },
            { title: '运单号', dataIndex: 'shipmentId', width: 140, ellipsis: true, render: (v) => v || '-' },
            { title: '创建时间', dataIndex: 'createTimestamp', width: 160, render: (t) => (t ? new Date(t).toLocaleString() : '-') },
            { title: '操作', key: 'action', width: 140, fixed: 'right', render: (_, record) => (
              <Space onClick={(e) => e.stopPropagation()}>
                <Button type="link" size="small" onClick={() => openDetail(record)}>详情</Button>
                {record.state === 'preparing' && record.id && (
                  <Button type="link" size="small" onClick={(e) => { e.stopPropagation(); openReceiveModal([record.id!]); }}>收货</Button>
                )}
                <Tooltip title="敬请期待"><Button type="link" size="small" disabled>交代理仓</Button></Tooltip>
              </Space>
            ) },
          ]}
        />
      </Card>

      <Modal
        title="录入货物"
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <p style={{ marginBottom: 12, color: '#666' }}>手动录入一箱或多箱货物，录入后状态为「待收货」。从装箱单导入功能敬请期待。</p>
        {cartonRows.map((row, cartonIndex) => (
          <Card key={cartonIndex} size="small" title={`第 ${cartonIndex + 1} 箱`} style={{ marginBottom: 12 }} extra={cartonRows.length > 1 ? <Button type="link" danger size="small" onClick={() => removeCartonRow(cartonIndex)}>删除本箱</Button> : null}>
            <Form layout="vertical" style={{ marginBottom: 8 }}>
              <Form.Item label="货物标识（必填）">
                <Input
                  placeholder="如：PO001-1"
                  value={row.cartonId}
                  onChange={(e) => onAddCartonId(cartonIndex, e.target.value)}
                />
              </Form.Item>
              <Form.Item label="供应商">
                <Select
                  placeholder="选择供应商"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={suppliers}
                  value={row.fromSupplierId}
                  onChange={(v) => onAddSupplier(cartonIndex, v)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="客户">
                <Select
                  placeholder="选择客户"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={customers}
                  value={row.belongClientId}
                  onChange={(v) => onAddClient(cartonIndex, v)}
                  style={{ width: '100%' }}
                />
              </Form.Item>
              <Form.Item label="产品（海关编码 + 数量）">
                {(row.products || [{ hscode: '', amount: 1 }]).map((p, pi) => (
                  <Space key={pi} style={{ marginBottom: 8 }}>
                    <Input
                      placeholder="海关编码"
                      value={p.hscode}
                      onChange={(e) => {
                        const next = [...(row.products || [])];
                        if (!next[pi]) next[pi] = { hscode: '', amount: 1 };
                        next[pi] = { ...next[pi], hscode: e.target.value };
                        onAddProductsChange(cartonIndex, next);
                      }}
                      style={{ width: 140 }}
                    />
                    <InputNumber
                      placeholder="数量"
                      min={1}
                      value={p.amount ?? 1}
                      onChange={(v) => {
                        const next = [...(row.products || [])];
                        if (!next[pi]) next[pi] = { hscode: '', amount: 1 };
                        next[pi] = { ...next[pi], amount: v ?? 1 };
                        onAddProductsChange(cartonIndex, next);
                      }}
                      style={{ width: 80 }}
                    />
                    {(row.products?.length ?? 1) > 1 && (
                      <Button type="link" danger size="small" onClick={() => onAddProductsChange(cartonIndex, (row.products || []).filter((_, i) => i !== pi))}>删除</Button>
                    )}
                  </Space>
                ))}
                <Button type="dashed" size="small" onClick={() => onAddProductsChange(cartonIndex, [...(row.products || []), { hscode: '', amount: 1 }])}>+ 添加产品</Button>
              </Form.Item>
            </Form>
          </Card>
        ))}
        <Space style={{ marginTop: 8 }}>
          <Button onClick={addCartonRow}>+ 再录一箱</Button>
          <Button type="primary" loading={submitting} onClick={submitAdd}>确认录入</Button>
          <Button onClick={() => setAddModalOpen(false)}>取消</Button>
        </Space>
      </Modal>

      <Modal
        title={receiveTargetIds.length > 1 ? `批量收货（${receiveTargetIds.length} 箱）` : '收货'}
        open={receiveModalOpen}
        onCancel={closeReceiveModal}
        footer={[
          <Button key="cancel" onClick={closeReceiveModal}>取消</Button>,
          <Button key="submit" type="primary" loading={receiveSubmitting} onClick={submitReceive}>确认收货</Button>,
        ]}
        width={560}
        destroyOnClose
      >
        <Form form={receiveForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="arrivedBy" label="到达方式" rules={[{ required: true, message: '请选择到达方式' }]}>
            <Select
              placeholder="请选择"
              options={[
                { value: 0, label: '货拉拉' },
                { value: 1, label: '快递' },
              ]}
            />
          </Form.Item>
          <Form.Item name="queryParamOfShippment" label="查询单号">
            <Input placeholder="货拉拉或快递单号" />
          </Form.Item>
          <Form.Item label="照片">
            {receivePictures.map((p, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                <Input
                  placeholder="图片标注，如货拉拉收货照片"
                  value={p.desc}
                  onChange={(e) => setReceivePictureDesc(idx, e.target.value)}
                  style={{ width: 180 }}
                />
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                      const data = reader.result as string;
                      setReceivePictureBase64(idx, data);
                    };
                    reader.readAsDataURL(file);
                    return false; // 阻止自动上传
                  }}
                >
                  <Button size="small">{p.base64 ? '更换图片' : '选择图片'}</Button>
                </Upload>
                {p.base64 && <span style={{ color: '#52c41a', fontSize: 12 }}>已选</span>}
                <Button type="link" danger size="small" onClick={() => removeReceivePicture(idx)}>删除</Button>
              </div>
            ))}
            <Button type="dashed" size="small" onClick={addReceivePicture}>+ 添加照片</Button>
          </Form.Item>
        </Form>
      </Modal>

      <CartonDetailDrawer
        open={detailDrawerOpen}
        cartonId={detailCartonId}
        onClose={closeDetail}
        onSaved={() => load()}
      />
    </>
  );
};

export default CartonList;
